/* eslint-disable max-len */
import React from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
  Select,
  TextField,
  Button,
  Skeleton,
  Stack,
  Paper,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import DialogAction from '../components/Dialog/DialogAction';
import Theme from '../Themes/main.theme';
import PersonIcon from '@mui/icons-material/Person';
import {
  RequestDict,
  RequestStatus,
} from '../components/dictionaries/RequestStatus';
import { VisibilityDict } from '../components/dictionaries/LocationVisibility';
import GMapsPlaceMarker from '../components/Maps/GMapsPlaceMarker';
import GMapsPlaceCircle from '../components/Maps/GMapsPlaceCircle';
import {
  arrayDetailsFormat,
  formatAzureFiles,
  formatMediaData,
  formatMediaUrlsToCompressed,
  transformGreekToEnglish,
} from '../lib/helperFuncs';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import BackButton from '../components/Buttons/BackButton';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import HttpService from '../services/HttpService';
import ConditionalWrapper from '../components/ConditionalWrapper/ConditionalWrapper';
import Attachments from '../components/Attachments/Attachments';
import { Title_Naming } from '../lib/generalTypes';
import UserService from '../services/UserService';
import { AdminAnswerApplicationPermissions, IntExt } from '../lib/permissions';
import PageviewIcon from '@mui/icons-material/Pageview';
import HorizontalWrapper from '../components/Details/HorizonticalWrapper';
import Details from '../components/Details/Details';
import DetailsWrapper from '../components/Details/DetailsWrapper';
import AsyncBackdropSubmit from '../components/AsyncBackdropSubmit';
import LinesSkeleton from '../components/Skeletons/LinesSkeleton';
import { getAllUsers } from '../lib/static/users/fetchers';
import HorizontalStepperWithError from '../components/StepperQueue';
import { useQueueState } from '../hooks/useQueueState';
import i18n from '../i18n';
import { basicUserDbInfo } from '../hooks/UserInfo';
import CarouselContainerForDetails from '../components/Carousel/CarouselContainerForDetails';

interface ISubmit {
  status?: string;
  responsible_user_id?: number | undefined | null;
}

interface IData {
  title: string;
  category: string;
  info: string;
  info_en: string;
  amea_access: boolean;
  overweight_vehicle_access: boolean;
  restrictions: string;
  restrictions_en: string;
  tags: string[];
  tags_gr: string[];
  show_on_map: 'visible' | 'hidden' | 'sample';
  city: string;
  tk: string;
  address: string;
  address_number: string;
  name: string;
  lastname: string;
  mobile: string;
  email: string;
  website: string;
  latitude: number | null;
  longitude: number | null;
  thumbnail: string;
  responsible_user_id: number | null;
  is_request_responsible: boolean;
  application_id: number | null;
  protocol_number: number | null;
  submit_date: string;
  status: string;
  full_name: string;
  section: string;
  office: string;
  administrative_unit: string;
  administrative_unit_id: number | null;
  applicant_id: number | null;
  applicant: string;
  location_id: number | null;
  comments: { comment: string; date_created: string }[];
  last_updated: string;
  attached_files: string[];
  media: string[];
  type_of_application?: Title_Naming;
  office_id: number | null;
  licensing: string;
  licensing_en: string;
  applicant_role: string;
}

const ViewApplication = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [dialogMode, setDialogMode] = React.useState('');
  const [text, setText] = React.useState('');
  const [newUser, setNewUser] = React.useState<number | undefined | null>();
  const [media, setMedia] = React.useState<any[]>([]);
  const [users, setUsers] = React.useState<
    {
      key: string | null;
      value: number | null | undefined | string;
    }[]
  >([]);

  // const [activeQueueStep, setActiveQueueStep] = React.useState<number>(0);
  // const [stepFailed, setStepFailed] = React.useState<number | null>(null);

  const [shouldRenderActionButtons, setShouldRenderActionButtons] =
    React.useState<boolean>(false);

  const is_external = UserService.hasRole([IntExt.External]);

  const [viewData, setViewData] = React.useState<IData>({
    title: '',
    category: '',
    info: '',
    info_en: '',
    amea_access: false,
    overweight_vehicle_access: false,
    restrictions: '',
    restrictions_en: '',
    tags: [],
    tags_gr: [],
    show_on_map: 'hidden',
    city: '',
    tk: '',
    address: '',
    address_number: '',
    name: '',
    lastname: '',
    mobile: '',
    email: '',
    website: '',
    latitude: null,
    longitude: null,
    thumbnail: '',
    responsible_user_id: null,
    is_request_responsible: false,
    application_id: null,
    protocol_number: null,
    submit_date: '',
    status: 'draft',
    full_name: '',
    section: '',
    office: '',
    administrative_unit: '',
    administrative_unit_id: null,
    applicant_id: null,
    applicant: '',
    location_id: null,
    comments: [],
    last_updated: '',
    attached_files: [],
    media: [],
    office_id: null,
    licensing: '',
    licensing_en: '',
    applicant_role: '',
  });

  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [canInteractWithApplication, setCanInteractWithApplication] =
    React.useState<boolean>(false);
  const { pathname } = useLocation();
  const { basicUserInfo, loading: userInfoLoading } = basicUserDbInfo();

  document.title =
    viewData.type_of_application !== Title_Naming.UpdateLocation
      ? viewData.applicant_role === IntExt.External
        ? t('Αίτημα Καταχώρισης Τοποθεσίας')
        : t('Εσωτερικό Αίτημα Καταχώρισης Τοποθεσίας')
      : t('Αίτημα Αλλαγής Στοιχείων Τοποθεσίας');

  const getData = async () => {
    try {
      const respApplicationLocation = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/applications/locations/${id}`
      );
      setViewData(respApplicationLocation.data);
      setShouldRenderActionButtons(
        respApplicationLocation.data?.is_request_responsible &&
        !(
          respApplicationLocation.data.status === RequestStatus.Cancelled ||
          respApplicationLocation.data.status === RequestStatus.Successful ||
          respApplicationLocation.data.status === RequestStatus.Rejected
        )
      );

      const { images, videos, thumbnail } = formatMediaData({
        ...respApplicationLocation.data,
        thumbnail: respApplicationLocation.data.thumbnail.replace(
          'original',
          'compressed'
        ),
        media: formatMediaUrlsToCompressed(respApplicationLocation.data.media),
      });

      setMedia([thumbnail, ...images, ...videos]);

      if (is_external) return;
      const respUnit: any = await getAllUsers({
        administrative_unit_id:
          respApplicationLocation.data.administrative_unit_id,
        is_enabled: true,
      });
      const respPublishers: any = await getAllUsers({
        'urm.role_id': 'f4bbf0e8-fe6b-472e-908e-515c53bc1602', // this is about EKOME publishers
        is_enabled: true,
      });

      const resp = {
        data: [
          ...respUnit.data.map((x: any) => {
            return {
              ...x,
              full_name: `${x?.full_name} (Οργανική Μονάδα)`,
            };
          }),
          ...respPublishers.data.map((x: any) => {
            return {
              ...x,
              full_name: `${x?.full_name} (Publisher)`,
            };
          }),
        ],
        total_count: respUnit?.total_count + respPublishers?.total_count,
      };

      setUsers(
        resp.data.map((user: any) => {
          return {
            key: user.full_name,
            value: user.id,
          };
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (userInfoLoading || isLoading)
      return setCanInteractWithApplication(false);

    if (
      UserService.hasRole([
        AdminAnswerApplicationPermissions.PermissionAnswerLocationAll,
      ])
    )
      return setCanInteractWithApplication(true);

    return setCanInteractWithApplication(
      UserService.hasRole([
        AdminAnswerApplicationPermissions.PermissionAnswerLocationMyOrg,
      ]) &&
      basicUserInfo.administrative_unit_id === viewData.administrative_unit_id
    );
  }, [isLoading, userInfoLoading]);

  React.useEffect(() => {
    getData();
  }, [shouldRenderActionButtons]);

  const basic_info = [
    [
      {
        name: 'id',
        value: String(viewData.application_id),
      },
      {
        name: t('Αρμόδια Οργανική Μονάδα'),
        value: viewData.administrative_unit_id
          ? (
            is_external
              ? (
                i18n.language === 'el'
                  ? (
                    viewData.administrative_unit
                  )
                  : (
                    transformGreekToEnglish(t(viewData.administrative_unit))
                  )
              )
              : (
                <>
                  {' '}
                  <Stack direction='row'>
                    <Typography>
                      {i18n.language === 'el'
                        ? viewData.administrative_unit
                        : transformGreekToEnglish(t(viewData.administrative_unit))}
                    </Typography>
                    <Tooltip
                      PopperProps={{
                        disablePortal: true,
                      }}
                      title={t('Προβολή')}
                    >
                      <IconButton
                        color='primary'
                        size='small'
                        onClick={() => {
                          navigate(
                            `/admin/units/${viewData.office}/${viewData.office_id}`
                          );
                        }}
                      >
                        <PageviewIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </>
              )
          )
          : (
            '-'
          ),
      },
      {
        name: t('Τομέας'),
        value:
          i18n.language === 'el'
            ? viewData.section
            : transformGreekToEnglish(t(viewData.section)),
      },
    ],
    [
      {
        name: t('Αριθμός Πρωτοκόλλου'),
        value: viewData.protocol_number
          ? String(viewData.protocol_number)
          : '-',
      },
      ...(!is_external
        ? [
          {
            name: t('Υπεύθυνος Χειριστής'),
            value: (
              <Box display='flex'>
                {viewData.responsible_user_id
                  ? (
                    <>
                      <Stack direction='row'>
                        <Typography>
                          {i18n.language === 'el'
                            ? viewData.full_name
                            : transformGreekToEnglish(t(viewData.full_name))}
                        </Typography>
                        <Tooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          title={t('Προβολή')}
                        >
                          <IconButton
                            color='primary'
                            size='small'
                            onClick={() => {
                              navigate(
                                `/admin/users/${viewData.responsible_user_id}`
                              );
                            }}
                          >
                            <PageviewIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </>
                  )
                  : (
                    <Box mr={2}>-</Box>
                  )}
                {/* if (FO_Editor and !responsible_user_id) */}
                <Tooltip
                  PopperProps={{
                    disablePortal: true,
                  }} //title: Αναληψη Αιτηματος if FO_Editor
                  title={t('Αλλαγή Χρήστη')}
                >
                  {/* if FO_Editor handleUserChange() else setOpen(true)*/}
                  <IconButton
                    color='primary'
                    size='small'
                    disabled={
                      viewData?.status === RequestStatus.Draft ||
                      viewData?.status === RequestStatus.Completed ||
                      viewData?.status === RequestStatus.Successful ||
                      viewData?.status === RequestStatus.Rejected
                    }
                    onClick={() => {
                      setOpen(true);
                      setDialogMode('select');
                    }}
                  >
                    <PersonIcon fontSize='inherit' />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
          },
        ]
        : []),
      {
        name: t('Λογαριασμός Αιτούντος'),
        value: viewData?.applicant_id
          ? (
            <>
              <Stack direction='row'>
                <Typography>
                  {i18n.language === 'el'
                    ? viewData.applicant
                    : transformGreekToEnglish(t(viewData.applicant))}
                </Typography>
                {!is_external && (
                  <Tooltip
                    PopperProps={{
                      disablePortal: true,
                    }}
                    title={t('Προβολή')}
                  >
                    <IconButton
                      color='primary'
                      size='small'
                      onClick={() => {
                        pathname.split('/').filter(Boolean)[0] === 'admin'
                          ? viewData.applicant_role === IntExt.External
                            ? navigate(`/admin/visitor/${viewData?.applicant_id}`)
                            : navigate(`/admin/users/${viewData?.applicant_id}`)
                          : navigate(
                            `/visitor/details/${viewData?.applicant_id}`
                          );
                      }}
                    >
                      <PageviewIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </>
          )
          : (
            '-'
          ),
      },
    ],
    [
      {
        name: t('Ημερομηνία Υποβολής'),
        value: moment(viewData.submit_date).format('DD/MM/YYYY'),
      },
      {
        name: t('Κατάσταση'),
        value:
          i18n.language === 'el'
            ? RequestDict[viewData?.status]
            : transformGreekToEnglish(t(RequestDict[viewData?.status])),
      },
      {
        name: t('Τελευταία αλλαγή κατάστασης'),
        value: moment(viewData.last_updated).format('DD/MM/YYYY'),
      },
    ],
  ];
  const basic_info_details = [
    [
      {
        name: is_external
          ? t('Τίτλος Ιδιοκτησίας')
          : t('Τίτλος Τοποθεσίας'),
        value: (
          <>
            {i18n.language === 'el'
              ? viewData?.title
              : transformGreekToEnglish(t(viewData?.title))}
          </>
        ),
      },
      {
        name: is_external
          ? t('Κατηγορία Ιδιοκτησίας')
          : t('Κατηγορία Tοποθεσίας'),
        value: viewData?.category || '-',
      },
      ...(i18n.language !== 'en'
        ? [
          {
            name: is_external
              ? t('Πληροφορίες Ιδιοκτησίας')
              : t('Πληροφορίες Τοποθεσίας'),
            value:
              i18n.language === 'el'
                ? viewData?.info
                : transformGreekToEnglish(t(viewData?.info)),
          },
        ]
        : []),
      {
        name: is_external
          ? t('Πληροφορίες Ιδιοκτησίας')
          : t('Πληροφορίες Τοποθεσίας'),
        value:
          i18n.language === 'el'
            ? viewData?.info_en
            : transformGreekToEnglish(t(viewData?.info_en)),
      },
      ...(i18n.language !== 'en'
        ? [
          {
            name: t('Πληροφορίες Αδειοδότησης'),
            value: viewData?.licensing || '-',
          },
        ]
        : []),
      ...(i18n.language !== 'el'
        ? [ {
          name: t('Πληροφορίες Αδειοδότησης'),
          value: viewData?.licensing_en || '-',
        },
        ]
        : []),
    ],
    [
      {
        name: t('Πρόσβαση σε άτομα με ειδικές ανάγκες'),
        value:
          viewData?.amea_access === null
            ? '-'
            : viewData?.amea_access
              ? t('ΝΑΙ')
              : t('ΟΧΙ'),
      },
      {
        name: t('Πρόσβαση σε βαρέα οχήματα'),
        value:
          viewData?.overweight_vehicle_access === null
            ? '-'
            : viewData?.overweight_vehicle_access
              ? t('ΝΑΙ')
              : t('ΟΧΙ'),
      },
      ...(i18n.language !== 'en'
        ? [
          {
            name: t('Περιορισμοί'),
            value: viewData?.restrictions || '-',
          },
        ]
        : []),
      {
        name: t('Περιορισμοί'),
        value: viewData?.restrictions_en || '-',
      },
      ...(i18n.language !== 'en' || !is_external
        ? [
          {
            name: t('Λέξεις - Κλειδιά στα Ελληνικά'),
            value:
              viewData?.tags_gr && viewData?.tags_gr?.length > 0
                ? viewData?.tags
                : '-',
          },
        ]
        : []),
      {
        name: t('Λέξεις - Κλειδιά'),
        value:
          viewData?.tags && viewData?.tags?.length > 0
            ? viewData?.tags_gr
            : '-',
      },
    ],
  ];
  const address_info_details = [
    {
      name: t('Εμφάνιση στίγματος στον χάρτη'),
      value:
        i18n.language === 'el'
          ? VisibilityDict[viewData?.show_on_map]
          : transformGreekToEnglish(t(VisibilityDict[viewData?.show_on_map])),
    },
    {
      name: t('Περιφέρειες / Δήμοι'),
      value:
        i18n.language === 'el'
          ? viewData?.city
          : transformGreekToEnglish(t(viewData?.city)),
    },
    {
      name: t('ΤΚ'),
      value: String(viewData?.tk),
    },
    {
      name: t('Οδός'),
      value:
        i18n.language === 'el'
          ? viewData?.address
          : transformGreekToEnglish(t(viewData?.address)) || '-',
    },
    {
      name: t('Αριθμός'),
      value: viewData?.address_number || '-',
    },
  ];
  const resp_usr_info_details = [
    ...(is_external
      ? [
        {
          name: t('Όνομα'),
          value: viewData?.name,
        },
      ]
      : []),
    ...(is_external
      ? [
        {
          name: t('Επώνυμο'),
          value: viewData?.lastname,
        },
      ]
      : []),
    {
      name: t('Τηλέφωνο'),
      value: viewData?.mobile || '-',
    },
    {
      name: 'Email',
      value: viewData?.email || '-',
    },
    {
      name: 'Website',
      value: viewData?.website || '-',
    },
  ];
  const otherDocs = (
    <Box>
      <Typography variant='h3' style={{ marginLeft: '10px' }}>
        {t('Άλλα Επισυναπτόμενα Έγγραφα')}
      </Typography>
      <Attachments attachments={formatAzureFiles(viewData?.attached_files)} />
    </Box>
  );

  const SubmitValue = async ({ status, responsible_user_id }: ISubmit) => {
    try {
      status && setOpenBackdrop(true);
      const resp = await HttpService.getAxiosClient().put(
        `${process.env.REACT_APP_BACKEND_URL}/applications/locations/${id}`,
        {
          ...(status && {
            status: status,
            ...(text && {
              free_text: text,
            }),
          }),
          ...(responsible_user_id && {
            responsible_user_id: newUser,
          }),
        }
      );
      if (resp.status === 200) {
        if (responsible_user_id) {
          const user = users?.find(
            (user) => user.value === responsible_user_id
          );
          setViewData({
            ...(viewData as IData),
            responsible_user_id: Number(user?.value),
            full_name: user?.key || '',
          });
          setShouldRenderActionButtons(resp.data.is_responsible);
        }
        enqueueSnackbar(resp.data.message, { variant: resp.data.action });
        if (status) {
          if (pathname.split('/').filter(Boolean)[0] === 'admin') {
            navigate('/admin/applications/dashboard');
          } else navigate('/my-applications');
        }
      }
    } catch (error) {
      enqueueSnackbar(
        t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα'),
        { variant: 'error' }
      );
      console.error(error);
    }
    setOpenBackdrop(false);
  };

  const handleDialogProps = () => {
    switch (dialogMode) {
    case 'select':
      return {
        ...(users.length === 0 && {
          mode: 'info' as 'info' | 'delete' | 'edit',
        }),
        handleClose: () => {
          setOpen(false);
          setNewUser(viewData?.responsible_user_id);
        },
        title: t('Αλλαγή ανάθεσης σε'),
        content:
            users.length === 0
              ? (
                t(
                  'Δεν υπάρχουν διαθέσιμοι Ενεργοί Χρήστες στην συγκεκριμένη Οργανική Μονάδα'
                )
              )
              : (
                <Select
                  MenuProps={{
                    disablePortal: true,
                  }}
                  fullWidth
                  value={newUser}
                  onChange={(e) => setNewUser(e.target.value as number)}
                >
                  {users.map((option: any) => {
                    return (
                      <MenuItem key={option.value} value={option.value}>
                        {option.key}
                      </MenuItem>
                    );
                  })}
                </Select>
              ),
        onAccept: () => {
          SubmitValue({ responsible_user_id: newUser });
          setOpen(false);
        },
      };
    case 'rejected':
      return {
        fullWidth: true,
        open: open,
        handleClose: () => {
          setOpen(false);
          setText(''); //reset
        },
        title: t('Δικαιολόγηση Απόρριψης'),
        content: (
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder={
                t(
                  'Ενημερώστε τον χρήστη για τους λόγους απόρριψης του αιτήματος.'
                ) as string
            }
          />
        ),
        onAccept: () => {
          SubmitValue({ status: dialogMode });
          setOpen(false);
        },
        disableAcceptButton: !text,
      };
    case 'under_update':
      return {
        fullWidth: true,
        open: open,
        handleClose: () => {
          setOpen(false);
          setText(''); //reset
        },
        title: t('Δικαιολόγηση Επικαιροποίησης'),
        content: (
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder={
                t(
                  'Ενημερώστε τον χρήστη για τις αλλαγές που χρειάζεται να πραγματοποιήσει.'
                ) as string
            }
          />
        ),
        onAccept: () => {
          SubmitValue({ status: dialogMode });
          setOpen(false);
        },
        disableAcceptButton: !text,
      };
    case 'successful':
      return {
        fullWidth: true,
        open: open,
        handleClose: () => {
          setOpen(false);
          setText(''); //reset
        },
        title: t('Προαιρετική Προσθήκη Σχολίων'),
        content: (
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder={
                t(
                  'Προσθέστε εάν κρίνετε απαραίτητο κάποια σχόλια σχετικά με την συγκεκριμένη καταχώριση.'
                ) as string
            }
          />
        ),
        onAccept: () => {
          SubmitValue({ status: dialogMode });
          setOpen(false);
        },
      };
    default:
      return {
        handleClose: () => null,
        title: '',
        content: <></>,
        onAccept: () => null,
      };
    }
  };

  const { activeQueueStep, stepFailed } = useQueueState(id);

  {
    /* if in my administrative_unit */
  }
  return (
    <>
      <AsyncBackdropSubmit isLoading={openBackdrop}>
        {t('Παρακαλώ περιμένετε καθώς επιβεβαιώνουμε τα στοιχεία σας')}
      </AsyncBackdropSubmit>
      <Box mt={2} mb={2}>
        <BackButton
          onClick={() =>
            pathname.split('/').filter(Boolean)[0] === 'admin'
              ? navigate('/admin/applications/dashboard')
              : navigate('/my-applications')
          }
        />
      </Box>
      <Typography variant='h2' color={Theme.labelColor?.main}>
        {viewData.type_of_application !== Title_Naming.UpdateLocation
          ? viewData.applicant_role === IntExt.External
            ? t('Αίτημα Καταχώρισης Τοποθεσίας')
            : t('Εσωτερικό Αίτημα Καταχώρισης Τοποθεσίας')
          : t('Αίτημα Αλλαγής Στοιχείων Τοποθεσίας')}
      </Typography>

      {!is_external && viewData.status == RequestStatus.In_Progress && (
        <>
          <Box
            sx={{
              backgroundColor: Theme.labelColor?.background,
              py: 1,
              pl: 1,
              ml: 1,
              my: 2,
            }}
          >
            <Typography variant='h3' color={Theme.labelColor?.main}>
              {t('Κατάσταση Αιτήματος για εισαγωγή στο Omeka')}
            </Typography>
          </Box>
          <HorizontalStepperWithError
            activeStep={activeQueueStep}
            failedStep={stepFailed}
          />
        </>
      )}

      {/* Basic info of the Request */}
      <Box>
        <Box
          sx={{
            backgroundColor: Theme.labelColor?.background,
            py: 1,
            pl: 1,
            ml: 1,
            my: 2,
          }}
        >
          <Typography variant='h3' color={Theme.labelColor?.main}>
            {t('Βασικές Πληροφορίες Αιτήματος')}
          </Typography>
        </Box>
        {isLoading
          ? (
            <LinesSkeleton lines={3} />
          )
          : (
            <HorizontalWrapper title={''} details={basic_info}
              marginLeft />
          )}
      </Box>
      {!isLoading &&
        (viewData.status === RequestStatus.Under_Update ||
          viewData.status === RequestStatus.Successful ||
          viewData.status === RequestStatus.Rejected) &&
        viewData.comments.length > 0
        ? (
          <>
            <Box
              sx={{
                backgroundColor: Theme.labelColor?.background,
                py: 1,
                pl: 1,
                ml: 1,
                my: 2,
              }}
            >
              <Typography variant='h3' color={Theme.labelColor?.main}>
                {t('Σχόλια από Υπεύθυνο Χειριστή')}
              </Typography>
            </Box>
            <Box
              display='flex'
              flexDirection='column'
              height='100px'
              overflow='auto'
              marginLeft='16px'
              gap={2}
            >
              {viewData?.comments?.map((comm) => {
                return (
                  <Box key={JSON.stringify(comm)}>
                    <Typography fontWeight='500'>
                      {moment(comm.date_created).format('DD/MM/YYYY hh:mm:ss')}
                    </Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {comm.comment}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )
        : null}
      {/* Request Details */}
      <Box>
        <Box
          sx={{
            backgroundColor: Theme.labelColor?.background,
            py: 1,
            pl: 1,
            ml: 1,
            my: 2,
          }}
        >
          <Typography variant='h3' color={Theme.labelColor?.main}>
            {t('Λεπτομέρειες Αιτήματος')}
          </Typography>
        </Box>
        {isLoading
          ? (
            <LinesSkeleton lines={5} />
          )
          : (
            <>
              <HorizontalWrapper
                renderArrays={(array) => arrayDetailsFormat(array)}
                title={
                  is_external
                    ? t('Βασικά Στοιχεία Ιδιοκτησίας')
                    : t('Βασικά Στοιχεία Τοποθεσίας')
                }
                details={basic_info_details}
                marginLeft
              />
              <DetailsWrapper
                title={t('Στοιχεία Υπεύθυνου Επικοινωνίας')}
                detailsProps={{
                  rows: resp_usr_info_details,
                  removeLines: true,
                }}
                marginLeft
              />{' '}
            </>
          )}
        <Box sx={{ ml: '10px', mt: 2 }}>
          <Paper
            variant='outlined'
            sx={{
              minWidth: '200px',
              height: 'fit-content',
              borderRadius: 2,
              boxShadow: 0,
              backgroundColor: 'transparent',
            }}
          >
            <Typography m={2} variant='h3'>
              {t('Διεύθυνση')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box m={2}>
                {isLoading
                  ? (
                    <LinesSkeleton lines={5} />
                  )
                  : (
                    <Details rows={address_info_details} removeLines />
                  )}
              </Box>
              <Box minWidth={{ xs: '90%', md: '50%' }} ml={6}
                m={2}>
                <ConditionalWrapper
                  condition={isLoading}
                  wrapper={(children) => (
                    <Skeleton variant='rectangular' width={500}>
                      {children}
                    </Skeleton>
                  )}
                >
                  {viewData?.show_on_map === 'sample'
                    ? (
                      <GMapsPlaceCircle
                        location={{
                          lat: Number(viewData?.latitude),
                          lng: Number(viewData?.longitude),
                        }}
                      />
                    )
                    : (
                      <GMapsPlaceMarker
                        zoom={12}
                        height='200px'
                        location={
                          viewData?.show_on_map === 'hidden'
                            ? undefined
                            : {
                              lat: Number(viewData?.latitude),
                              lng: Number(viewData?.longitude),
                            }
                        }
                      />
                    )}
                </ConditionalWrapper>
              </Box>
            </Box>
          </Paper>
        </Box>
        <Box sx={{ ml: '10px', mt: 2, minWidth: '220px' }}>
          <Paper
            variant='outlined'
            sx={{
              height: 'fit-content',
              borderRadius: 2,
              boxShadow: 0,
              backgroundColor: 'transparent',
            }}
          >
            <ConditionalWrapper
              condition={isLoading}
              wrapper={(children) => (
                <Box display='flex' justifyContent='center'
                  my={5}>
                  <Skeleton variant='rectangular' width='50%'
                    height={200}>
                    {' '}
                    {children}
                  </Skeleton>{' '}
                </Box>
              )}
            >
              <Box m={2}>
                <CarouselContainerForDetails
                  title={t('Φωτογραφίες / Βίντεο') as string}
                  media={media}
                  imageButtons={false}
                />
              </Box>
            </ConditionalWrapper>
          </Paper>
        </Box>
      </Box>
      {viewData?.attached_files && viewData?.attached_files.length > 0
        ? otherDocs
        : null}
      {/* </Box> */}

      {/* Buttons */}
      {!is_external && (
        <Box m={5}>
          {!canInteractWithApplication && (
            <Typography textAlign={'center'} variant='h3'>
              {t(
                'Δεν έχετε δικαίωμα να  αλληλεπιδράσετε με την αίτηση, επικοινωνείστε με τον προϊστάμενο σας'
              )}
            </Typography>
          )}
        </Box>
      )}
      {shouldRenderActionButtons && canInteractWithApplication && (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5, gap: 2 }}
        >
          <Button
            disabled={
              stepFailed == -1 &&
              (activeQueueStep == 0 ||
                activeQueueStep == 1 ||
                activeQueueStep == 2)
            }
            variant='outlined'
            color='error'
            onClick={() => {
              setOpen(true);
              setDialogMode(RequestStatus.Rejected);
            }}
          >
            {t('Απόρριψη Καταχώρισης')}
          </Button>
          <Button
            disabled={
              stepFailed == -1 &&
              (activeQueueStep == 0 ||
                activeQueueStep == 1 ||
                activeQueueStep == 2)
            }
            variant='outlined'
            onClick={() => {
              setOpen(true);
              setDialogMode(RequestStatus.Under_Update);
            }}
          >
            {t('Επικαιροποίηση Καταχώρισης')}
          </Button>
          <Button
            disabled={
              stepFailed == -1 &&
              (activeQueueStep == 0 ||
                activeQueueStep == 1 ||
                activeQueueStep == 2)
            }
            variant='contained'
            onClick={() => {
              setOpen(true);
              setDialogMode(RequestStatus.Successful);
            }}
          >
            {t('Έγκριση Καταχώρισης')}
          </Button>
        </Box>
      )}
      {/* Modal */}
      <DialogAction open={open} {...handleDialogProps()} />
    </>
  );
};

export default ViewApplication;
